import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../../../services/assistantClient';
import { AssistantAccount as Account, LoginStart } from '../../../services/assistantClient';
import { isEnter } from '../../../utils/keyboard';
import { useTranslation } from 'react-i18next';

/**
 * AI 계정 연결 — 어시스턴트는 **사용자 본인 계정**으로 동작한다(서버 공용 키를 쓰지 않는다).
 *
 * 두 가지 방법이 있고 **로그인이 기본**이다:
 *   1) 로그인 — 서버가 CLI 로그인을 대신 몰고, 사용자는 자기 브라우저에서 승인만 한다.
 *      PC 에 CLI 를 깔 필요도, 터미널을 열 필요도 없고 만료되면 자동 갱신된다.
 *   2) 토큰 — `claude setup-token` 출력을 붙여넣는다. 브라우저를 못 여는 환경(폐쇄망·자동화)용.
 *      서버가 암호화해 보관한다.
 * 둘 다 있으면 로그인이 쓰인다 — 서버 threadRunner 의 선택 규칙과 같다.
 */

interface Props {
  account: Account;
  provider: string;
  onProviderChange: (p: string) => void;
  model: string;
  onModelChange: (m: string) => void;
  /** 계정 상태가 바뀌었을 때 패널이 다시 읽도록 */
  onChanged: (a: Account) => void;
  /** 이미 연결된 상태에서 열었을 때 닫기 (미연결이면 닫을 곳이 없다) */
  onClose?: () => void;
}

const PROVIDER_LABEL: Record<string, string> = {
  claude: 'Claude (Claude Code)',
  codex: 'Codex (ChatGPT)',
};

/** 제공자마다 모델 이름 체계가 다르다. 'default' 는 그 CLI 의 기본값을 그대로 쓴다는 뜻이다. */
const MODELS: Record<string, string[]> = {
  claude: ['default', 'sonnet', 'opus', 'haiku'],
  codex: ['default', 'gpt-5-codex', 'gpt-5'],
};

const AssistantAccountView: React.FC<Props> = ({ account, provider, onProviderChange, model, onModelChange, onChanged, onClose }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [login, setLogin] = useState<Partial<LoginStart> | null>(account.pendingLogin || null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [tokenOpen, setTokenOpen] = useState(false);
  const [token, setToken] = useState('');
  const [notice, setNotice] = useState('');

  const refresh = useCallback(async () => {
    try { onChanged(await api.getAccount(provider)); } catch { /* 다음 시도에 다시 본다 */ }
  }, [provider, onChanged]);

  // 코드를 되돌려주지 않는 흐름(Codex 디바이스 인증·브라우저 자동 복귀)은 CLI 가 스스로 승인을 감지한다.
  // 요청을 몇 분씩 붙들지 않도록 화면이 계정 상태만 짧게 확인한다.
  useEffect(() => {
    if (!login || login.needsCode) return undefined;
    let stop = false;
    const timer = setInterval(async () => {
      if (stop) return;
      try {
        const a = await api.getAccount(provider);
        if (stop) return;
        onChanged(a);
        if (a.loggedIn) { stop = true; setLogin(null); setNotice(a.email ? t('assistant:account.connectedAs', { email: a.email }) : t('assistant:account.connected')); }
        else if (a.pendingLogin && a.pendingLogin.state === 'failed') {
          stop = true; setLogin(null); setError(a.pendingLogin.message || t('assistant:account.loginFailed'));
        } else if (!a.pendingLogin) { stop = true; setLogin(null); }
      } catch { /* 폴링 실패는 조용히 넘긴다 */ }
    }, 3000);
    return () => { stop = true; clearInterval(timer); };
  }, [login, provider, onChanged, t]);

  const beginLogin = async () => {
    // 클릭 **그 순간**에 빈 탭을 연다. await 뒤에 window.open 을 부르면 사용자 제스처가 끝난 뒤라
    // 브라우저가 조용히 막고, 화면에는 아무 일도 안 일어난 것처럼 보인다.
    let tab: Window | null = null;
    try { tab = window.open('', '_blank'); } catch { tab = null; }
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await api.startLogin(provider);
      setLogin(r);
      setCode('');
      if (tab && !tab.closed) tab.location.href = r.url;
      else setNotice(t('assistant:account.popupBlocked'));
    } catch (e: any) {
      if (tab && !tab.closed) tab.close();
      setError(e?.message || t('assistant:account.loginStartFailed'));
    } finally { setBusy(false); }
  };

  const finishLogin = async () => {
    if (!login?.loginId || !code.trim()) return;
    setBusy(true); setError('');
    try {
      const r = await api.submitLoginCode(login.loginId, code.trim());
      setLogin(null); setCode('');
      setNotice(r.email ? t('assistant:account.connectedAs', { email: r.email }) : t('assistant:account.connected'));
      await refresh();
    } catch (e: any) {
      setError(e?.message || t('assistant:account.codeFailed'));
    } finally { setBusy(false); }
  };

  const abortLogin = async () => {
    setLogin(null); setCode('');
    try { await api.cancelLogin(); } catch { /* 서버가 정리 못 해도 화면은 되돌린다 */ }
  };

  const doLogout = async () => {
    if (!window.confirm(t('assistant:account.logoutConfirm'))) return;
    setBusy(true);
    try { await api.logout(provider); setNotice(t('assistant:account.disconnected')); await refresh(); }
    catch (e: any) { setError(e?.message || t('assistant:account.logoutFailed')); }
    finally { setBusy(false); }
  };

  const doSaveToken = async () => {
    if (!token.trim()) return;
    setBusy(true); setError('');
    try {
      const r = await api.saveToken(token.trim(), provider);
      setToken(''); setTokenOpen(false);
      setNotice(t('assistant:account.tokenSaved', { hint: r.hint }));
      await refresh();
    } catch (e: any) {
      setError(e?.message || t('assistant:account.tokenSaveFailed'));
    } finally { setBusy(false); }
  };

  const doRemoveToken = async () => {
    setBusy(true);
    try { await api.removeToken(provider); setNotice(t('assistant:account.tokenRemoved')); await refresh(); }
    catch (e: any) { setError(e?.message || t('assistant:account.tokenRemoveFailed')); }
    finally { setBusy(false); }
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto p-3 text-[0.75rem] text-gray-700">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium text-gray-800">{t('assistant:account.title')}</span>
        {onClose && (
          <button type="button" onClick={onClose} className="text-[0.6875rem] text-gray-500 underline">{t('common:ui.close')}</button>
        )}
      </div>

      <p className="mb-2 text-[0.6875rem] leading-relaxed text-gray-500">
        {t('assistant:account.intro1')}<b>{t('assistant:account.introOwn')}</b>{t('assistant:account.intro2')}
      </p>

      {/* 제공자 */}
      <label className="mb-2 block">
        <span className="mb-0.5 block text-[0.6875rem] text-gray-500">{t('assistant:account.provider')}</span>
        <select
          value={provider}
          onChange={(e) => { onProviderChange(e.target.value); setLogin(null); setError(''); setNotice(''); }}
          disabled={busy}
          className="w-full rounded border border-gray-300 px-2 py-1 text-[0.75rem]"
        >
          {(account.providers || ['claude']).map((p) => (
            <option key={p} value={p}>{PROVIDER_LABEL[p] || p}</option>
          ))}
        </select>
      </label>

      {/* 모델 — 새 대화와 단발 작업(매핑 AI 보조)에 함께 쓰인다 */}
      <label className="mb-2 block">
        <span className="mb-0.5 block text-[0.6875rem] text-gray-500">{t('assistant:account.model')}</span>
        <select
          value={MODELS[provider]?.includes(model) ? model : 'default'}
          onChange={(e) => onModelChange(e.target.value)}
          disabled={busy}
          className="w-full rounded border border-gray-300 px-2 py-1 text-[0.75rem]"
        >
          {(MODELS[provider] || ['default']).map((m) => (
            <option key={m} value={m}>{m === 'default' ? t('assistant:account.defaultModel') : m}</option>
          ))}
        </select>
        <span className="mt-0.5 block text-[0.625rem] text-gray-400">{t('assistant:account.modelNote')}</span>
      </label>

      {/* 상태 */}
      <div className={`mb-2 rounded px-2 py-1 text-[0.6875rem] ${account.loggedIn ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
        {account.loggedIn ? (
          <>
            {t('assistant:account.connectedLine', { who: account.email || t('assistant:account.verified') })}
            {account.subscriptionType ? ` · ${account.subscriptionType}` : ''}
            {account.source === 'token' ? t('assistant:account.viaToken') : t('assistant:account.viaLogin')}
          </>
        ) : t('assistant:account.notConnected')}
        {account.mode === 'standalone' && <span className="ml-1 text-gray-500">{t('assistant:account.standalone')}</span>}
      </div>

      {notice && <div className="mb-2 rounded bg-blue-50 px-2 py-1 text-[0.6875rem] text-blue-700">{notice}</div>}
      {error && <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-[0.6875rem] text-red-700">{error}</div>}

      {/* 로그인 진행 중 */}
      {login ? (
        <div className="mb-2 rounded border border-blue-300 bg-blue-50 p-2">
          <div className="mb-1 text-[0.6875rem] font-medium text-blue-800">
            {login.needsCode ? t('assistant:account.pasteCode') : t('assistant:account.approve')}
          </div>
          {login.url && (
            <a href={login.url} target="_blank" rel="noreferrer"
              className="mb-1 block break-all text-[0.625rem] text-blue-700 underline">
              {login.url}
            </a>
          )}
          {login.code && !login.needsCode && (
            <div className="mb-1 text-[0.6875rem]">{t('assistant:account.displayCode')}<b className="font-mono">{login.code}</b></div>
          )}
          {login.needsCode ? (
            <div className="flex gap-1">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (isEnter(e)) finishLogin(); }}
                placeholder={t('assistant:account.codePh')}
                className="flex-1 rounded border border-blue-300 px-2 py-1 text-[0.6875rem]"
              />
              <button type="button" onClick={finishLogin} disabled={busy || !code.trim()}
                className="rounded bg-blue-600 px-2 py-1 text-[0.6875rem] text-white hover:bg-blue-700 disabled:bg-gray-300">
                {t('common:ui.confirm')}
              </button>
            </div>
          ) : (
            <div className="text-[0.6875rem] text-blue-700">{t('assistant:account.autoConnect')}</div>
          )}
          <button type="button" onClick={abortLogin} className="mt-1 text-[0.625rem] text-gray-500 underline">{t('common:ui.cancel')}</button>
        </div>
      ) : (
        <div className="mb-2 flex gap-1">
          <button type="button" onClick={beginLogin} disabled={busy || account.canLogin === false}
            className="rounded bg-blue-600 px-3 py-1 text-[0.6875rem] font-medium text-white hover:bg-blue-700 disabled:bg-gray-300">
            {account.loggedIn ? t('assistant:account.relogin') : t('assistant:account.login')}
          </button>
          {account.loggedIn && (
            <button type="button" onClick={doLogout} disabled={busy}
              className="rounded border border-gray-300 px-2 py-1 text-[0.6875rem] text-gray-600 hover:bg-gray-50">
              {t('assistant:account.disconnect')}
            </button>
          )}
        </div>
      )}

      {/* 토큰 (보조 수단) */}
      {account.supportsToken && account.mode === 'web' && (
        <div className="mt-3 border-t border-gray-200 pt-2">
          <button type="button" onClick={() => setTokenOpen((v) => !v)} className="text-[0.6875rem] text-gray-600 underline">
            {tokenOpen ? t('assistant:account.tokenCollapse') : t('assistant:account.tokenExpand')}
          </button>
          {tokenOpen && (
            <div className="mt-1 space-y-1">
              <p className="text-[0.625rem] leading-relaxed text-gray-500">
                {t('assistant:account.tokenHelp1')}<code className="rounded bg-gray-100 px-1">claude setup-token</code>{t('assistant:account.tokenHelp2')}
              </p>
              {account.credentialStoreConfigured === false && (
                <div className="rounded bg-amber-50 px-2 py-1 text-[0.625rem] text-amber-800">
                  {t('assistant:account.noKey')}
                </div>
              )}
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="sk-ant-..."
                className="w-full rounded border border-gray-300 px-2 py-1 text-[0.6875rem]"
              />
              <div className="flex items-center gap-1">
                <button type="button" onClick={doSaveToken} disabled={busy || !token.trim() || account.credentialStoreConfigured === false}
                  className="rounded bg-gray-700 px-2 py-1 text-[0.6875rem] text-white hover:bg-gray-800 disabled:bg-gray-300">
                  {t('assistant:account.register')}
                </button>
                {account.hasToken && (
                  <>
                    <span className="text-[0.625rem] text-gray-500">{t('assistant:account.registered', { hint: account.hint ? `(${account.hint})` : '' })}</span>
                    <button type="button" onClick={doRemoveToken} disabled={busy}
                      className="text-[0.625rem] text-gray-500 underline">{t('common:ui.delete')}</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssistantAccountView;
